import { Injectable } from '@angular/core';
import { TokenResult } from '@app/core/domain/auth';
import { LocalStorageService } from './local-storage.service';
import { SessionStorageService } from './session-storage.service';
import { PortalUserDto } from '../../domain/admin/models/portalUserDto';
import { NavigationItem } from '../../../theme/layout/admin/navigation/navigation';

const PORTAL_AUTH_TOKEN_KEY = 'portal-auth-token';
const PORTAL_AUTH_REFRESH_TOKEN_KEY = 'portal-refresh-token';
const PORTAL_AUTH_USER_KEY = 'portal-auth-user';
const PORTAL_APP_THEME_KEY = 'portal-app-theme';
const PORTAL_APP_HIDENAV_KEY = 'portal-app-hidenav';
const PORTAL_APP_MENU_KEY = 'app-menu';
const PORTAL_APP_INITIAL_URL_KEY = 'portal-app-initial-url';
const PORTAL_AUTH_ACCESS_TOKEN_KEY = 'portal-auth-access-token';
const PORTAL_AUTH_USERID_KEY = 'portal-auth-userid';
const PORTAL_AUTH_USERNAME_KEY = 'portal-auth-username';

@Injectable({
    providedIn: 'root',
})
export class PortalStorageService {
    constructor(private localStorageService: LocalStorageService, private sessionStorageService: SessionStorageService) {}

    //IMP: clearing local storage
    clearLocalStorage(): void {
        //Replace clear with individual key due to storage shared with forsite app
        this.localStorageService.remove(PORTAL_AUTH_TOKEN_KEY);
        this.localStorageService.remove(PORTAL_AUTH_USER_KEY);
        this.localStorageService.remove(PORTAL_AUTH_ACCESS_TOKEN_KEY);
        this.localStorageService.remove(PORTAL_AUTH_USERID_KEY);
        this.localStorageService.remove(PORTAL_AUTH_USERNAME_KEY);
        this.localStorageService.remove(PORTAL_APP_THEME_KEY);
        this.localStorageService.remove(PORTAL_APP_MENU_KEY);
        this.localStorageService.remove(PORTAL_APP_HIDENAV_KEY);
        this.localStorageService.remove(PORTAL_APP_INITIAL_URL_KEY);
        this.localStorageService.remove(PORTAL_AUTH_REFRESH_TOKEN_KEY);
    }

    clearAppAuthStorage(): void {
        //Replace clear with individual key due to storage shared with forsite app
        this.sessionStorageService.remove(PORTAL_AUTH_TOKEN_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_USER_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_ACCESS_TOKEN_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_USERID_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_USERNAME_KEY);
        this.sessionStorageService.remove(PORTAL_APP_THEME_KEY);
        this.sessionStorageService.remove(PORTAL_APP_MENU_KEY);
        this.sessionStorageService.remove(PORTAL_APP_HIDENAV_KEY);
        this.sessionStorageService.remove(PORTAL_APP_INITIAL_URL_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_REFRESH_TOKEN_KEY);
    }

    //#region local storage
    public saveToken(token: string): void {
        this.sessionStorageService.remove(PORTAL_AUTH_TOKEN_KEY);
        this.sessionStorageService.set(PORTAL_AUTH_TOKEN_KEY, token);
    }

    public getToken(): TokenResult {
        return this.sessionStorageService.get(PORTAL_AUTH_TOKEN_KEY);
    }

    public clearToken(): void {
        this.sessionStorageService.remove(PORTAL_AUTH_TOKEN_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_USER_KEY);
    }

    public saveRefreshToken(token: string): void {
        this.sessionStorageService.remove(PORTAL_AUTH_REFRESH_TOKEN_KEY);
        this.sessionStorageService.set(PORTAL_AUTH_REFRESH_TOKEN_KEY, token);
    }

    public getRefreshToken(): string {
        return this.sessionStorageService.get(PORTAL_AUTH_REFRESH_TOKEN_KEY);
    }

    public clearRefreshToken(): void {
        this.sessionStorageService.remove(PORTAL_AUTH_REFRESH_TOKEN_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_USER_KEY);
    }

    public saveUser(user): void {
        this.sessionStorageService.remove(PORTAL_AUTH_USER_KEY);
        this.sessionStorageService.set(PORTAL_AUTH_USER_KEY, user);
    }

    public getUser(): PortalUserDto {
        return this.sessionStorageService.get(PORTAL_AUTH_USER_KEY);
    }

    public saveTheme(theme: string): void {
        this.sessionStorageService.remove(PORTAL_APP_THEME_KEY);
        this.sessionStorageService.set(PORTAL_APP_THEME_KEY, theme);
    }

    public getTheme(): string {
        return this.sessionStorageService.get(PORTAL_APP_THEME_KEY);
    }

    public saveAccessToken(aToken: string): void {
        this.sessionStorageService.remove(PORTAL_AUTH_ACCESS_TOKEN_KEY);
        this.sessionStorageService.set(PORTAL_AUTH_ACCESS_TOKEN_KEY, aToken);
    }

    public getAccessToken(): string {
        return this.sessionStorageService.get(PORTAL_AUTH_ACCESS_TOKEN_KEY);
    }

    public saveUserId(userId: string): void {
        this.sessionStorageService.remove(PORTAL_AUTH_USERID_KEY);
        this.sessionStorageService.set(PORTAL_AUTH_USERID_KEY, userId);
    }

    public getUserId(): string {
        return this.sessionStorageService.get(PORTAL_AUTH_USERID_KEY);
    }

    public saveUserName(username: string): void {
        this.sessionStorageService.remove(PORTAL_AUTH_USERNAME_KEY);
        this.sessionStorageService.set(PORTAL_AUTH_USERNAME_KEY, username);
    }

    public getUserName(): string {
        return this.sessionStorageService.get(PORTAL_AUTH_USERNAME_KEY);
    }

    public removeAuth(): void {
        this.sessionStorageService.remove(PORTAL_AUTH_ACCESS_TOKEN_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_USERID_KEY);
        this.sessionStorageService.remove(PORTAL_AUTH_USERNAME_KEY);
    }

    public saveMenu(menu): void {
        this.sessionStorageService.remove(PORTAL_APP_MENU_KEY);
        this.sessionStorageService.set(PORTAL_APP_MENU_KEY, menu);
    }

    public getMenu(): NavigationItem[] {
        return this.sessionStorageService.get(PORTAL_APP_MENU_KEY);
    }
    //#endregion local storage

    //#region session storage
    public saveNav(nav: string): void {
        this.sessionStorageService.remove(PORTAL_APP_HIDENAV_KEY);
        this.sessionStorageService.set(PORTAL_APP_HIDENAV_KEY, nav);
    }

    public getNav(): string {
        return this.sessionStorageService.get(PORTAL_APP_HIDENAV_KEY);
    }

    public saveInitialUrl(currentUrl: string): void {
        this.sessionStorageService.remove(PORTAL_APP_INITIAL_URL_KEY);
        this.sessionStorageService.set(PORTAL_APP_INITIAL_URL_KEY, currentUrl);
    }

    public getInitialUrl(): string {
        return this.sessionStorageService.get(PORTAL_APP_INITIAL_URL_KEY);
    }

    //#endregion session storage
}
